import {
    GelLabel,
    GelParagraph,
    GelIcon,
    GelContainerLite,
    GelCollapsible,
    GelBoxLayout,
    GelRowLayout,
    GelScreenDetectorContext
} from "@tal-gel/components";
import { getGelTokens } from "@tal-gel/theming";
import { useCallback, useContext, useRef } from "react";
import Uploader from "./uploader";
import { AdobeClickType, PAYMENT_DETAILS_REQUIREMENTS, TFN_DETAILS_REQUIREMENTS, REQUIREMENTS_WITHOUT_UPLOAD } from "../../../constants/constants";
import OtherRequirement from "./otherRequirement";
import { useCookies } from "react-cookie";
import { AdobeAnalytics } from "../../../common/analytics/adobe-analytics";
import { MmcClaimDetailsItem } from "../../../common/models/contentful/claimDetails";
import { Requirements } from "../../../common/models/memberclaims";
import Tag from "../../../common/components/Tag";
import { activeUploadActions, useActiveUploadStore } from "./activeUploadStore";

interface RequirementDocumentsProps {
    requirement: Requirements;
    contentFullData: MmcClaimDetailsItem | undefined;
    memberNumber: string;
    superfundId: number;
}

const RequirementDocuments = ({ requirement, contentFullData, memberNumber }: RequirementDocumentsProps) => {
    const { global: { sizeBaseUnit, themeColorBorderDefault, themeColorIconDefault } } = getGelTokens()

    const [{ tpid }] = useCookies(['tpid']);
    const { screen, isXsScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;
    const activeId = useActiveUploadStore(s => s.activeRequirementId);

    const isOpen = activeId === requirement.id;

    const allowCollapseRef = useRef(true);
    const onDisableCollapse = useCallback((allowCollapse: boolean) => {
        allowCollapseRef.current = allowCollapse;
    }, []);

    const onHeaderClick = useCallback(() => {
        if (!allowCollapseRef.current) return;

        const opening = !isOpen;
        activeUploadActions.setActiveRequirement(opening ? (requirement.id ?? null) : null);

        activeUploadActions.consumeUploadComplete();

        AdobeAnalytics.PushClickEvent(tpid, requirement.description, AdobeClickType.Button);
    }, [isOpen, requirement.id, requirement.description, tpid]);

    return (
        <div id={requirement?.id?.toString()}>
            <GelContainerLite style={{
                borderTop: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderLeft: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderRight: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                width: "auto",
                cursor: "pointer"
            }}
                onClick={onHeaderClick}>
                <GelBoxLayout space={!isXsScreen(screen) ? [7, 0.5, 0.04] : [1, 0.01, 0.05]} gutter={sizeBaseUnit}
                    style={{
                        borderBottom: isOpen ? `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}` : "",
                        width: "auto",
                        padding: sizeBaseUnit * 4,
                        marginLeft: -sizeBaseUnit * 4,
                        marginRight: -sizeBaseUnit * 4
                    }}>

                    <GelLabel large>
                        {requirement.friendlyText}
                    </GelLabel>


                    {(requirement.receivedDate || requirement?.associatedDocuments?.length) &&
                        <Tag text={contentFullData?.requirementStatus?.RequirementInReview} status={"warning"} />
                    }

                    {!requirement.receivedDate && !requirement?.associatedDocuments?.length &&
                        <Tag text={contentFullData?.requirementStatus?.RequirementRequired} status={"danger"} />
                    }

                    <GelIcon name={isOpen ? "ChevronUp" : "ChevronDown"} color={themeColorIconDefault} width="20px" height="20px"></GelIcon>
                </GelBoxLayout>

            </GelContainerLite>

            <GelContainerLite style={{
                borderBottom: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderLeft: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderRight: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                width: "auto"
            }}>
                <GelCollapsible open={isOpen} >
                    <GelContainerLite style={{ paddingBottom: sizeBaseUnit * 2 }}>
                        <>
                            {(!contentFullData?.requirementsToHideUploadFunctionality?.Requirements?.includes(requirement.description) &&
                                !REQUIREMENTS_WITHOUT_UPLOAD.includes(requirement.friendlyText?.trim())) ?
                                <RequirementUploadDocument
                                    requirement={requirement}
                                    onDisableCollapse={onDisableCollapse} />
                                :
                                <RequiremntWithoutUploadDocument
                                    requirement={requirement} />}
                        </>

                        {PAYMENT_DETAILS_REQUIREMENTS.includes(requirement.description.toUpperCase()) &&
                            <div style={{
                                paddingTop: sizeBaseUnit * 2,
                                paddingBottom: sizeBaseUnit * 2,
                                marginLeft: -sizeBaseUnit * 4,
                                marginRight: -sizeBaseUnit * 4
                            }}>
                                <OtherRequirement key={requirement.id}
                                    contentFullData={contentFullData}
                                    requirement={requirement}
                                    memberNumber={memberNumber}
                                    requirementData={requirement}>
                                </OtherRequirement>
                            </div>
                        }

                        {TFN_DETAILS_REQUIREMENTS.includes(requirement.description) &&
                            <div style={{
                                paddingTop: sizeBaseUnit * 2,
                                paddingBottom: sizeBaseUnit * 2,
                                marginLeft: -sizeBaseUnit * 4,
                                marginRight: -sizeBaseUnit * 4
                            }}>
                                <OtherRequirement key={requirement.id}
                                    contentFullData={contentFullData}
                                    requirement={requirement}
                                    memberNumber={memberNumber}
                                    requirementData={requirement}>
                                </OtherRequirement>
                            </div>
                        }
                    </GelContainerLite>
                </GelCollapsible>
            </GelContainerLite>
        </div>
    );
};
export default (RequirementDocuments);


interface RequiremntWithoutUploadDocumentProps {
    requirement: Requirements;
}

const RequiremntWithoutUploadDocument = ({ requirement }: RequiremntWithoutUploadDocumentProps) => {
    const { global: { sizeBaseUnit, fontSizeBodyLarge } } = getGelTokens()
    const requirementExplanation = getRequirementExplanationWithoutUpload(requirement.friendlyText?.trim())
    return (<div data-testid="requirement-without-upload-document">
        <GelRowLayout gutter="xsmall">
            <GelParagraph style={{ fontWeight: '400px', fontSize: fontSizeBodyLarge, paddingTop: sizeBaseUnit * 4, paddingBottom: sizeBaseUnit * 2 }}>
                <div dangerouslySetInnerHTML={{ __html: requirementExplanation }} />
            </GelParagraph>
        </GelRowLayout>
    </div>)
}

interface RequirementUploadDocumentProps {
    requirement: Requirements;
    onDisableCollapse: (allowCollapse: boolean) => void;
}

const getRequirementExplanation = (requirement: string) => {
    switch (requirement) {
        case "Insurer Records":
            return "Please provide information about your claims from another insurer, including the amount and frequency of payments you've received so far. This will help us calculate the monthly benefit amount you may be eligible for from TAL. We may also need to request information from those insurers, but will let you know if we do.";
        case "Investigative or Diagnostic Tests":
            return "Please provide copies of any medical test results relevant to the condition you’re claiming for. This will help us understand the nature and extent of your condition so we can accurately assess your claim. Your treating doctor or clinic may be able to help gather this information.";
        case "Medicare/PBS Report":
            return "Please provide your Medicare and Pharmaceutical Benefits Scheme (PBS) history for the period we have outlined in our recent communication to you. This will help us understand the history of the condition you are claiming for and any other medical conditions that may impact the terms of your cover. Your Claims Consultant can help you with getting this information if you require assistance.";
        case "Payslips":
            return "Please provide copies of your payslips. This will help us calculate your benefit payments. Refer to our recent communication for specific details on the period we require Payslips."
                + "<br><br>For privacy protection, please make sure your Tax File Number (TFN) is removed or blacked out. ";
        case "Power of Attorney/ Letters of guardianship":
            return "If a Power of Attorney or Guardian has been appointed to act on your behalf, please provide the appropriate court-issued documentation. This will allow us to give them the applicable level of access to your claim details.";
        case "Proof of Insured Name and DOB":
            return "Please provide a certified copy of evidence of your full name and date of birth (e.g. Birth Certificate, Driver's Licence, Passport). This is required to confirm your identity and ensure that any claim information or benefit payments are released to an authorised person.";
        case "Proof of Pre-Disability Earnings":
            return "Please provide proof of your income for the 12 months prior to the date you were declared unfit for work (e.g. Payslips, ATO Notice of Assessment, Profit and Loss Statement). This will help us calculate the benefit amount you may be eligible to receive."
                + "<br><br> For privacy protection, please make sure your Tax File Number (TFN) is removed or blacked out. ";
        case "Tax Returns and Notice of Assessment":
            return "Please provide copies of your Individual or Company Tax Return and Notice of Assessment for the previous financial year. Refer to our recent communication for specific details on the period we require this information for."
                + "<br><br>There are multiple reasons we may need this information, depending on your claim circumstances."
                + "<br><br>In some cases, this will help us confirm your income for the 12 months prior to the date you were declared unfit for work and calculate the benefit amount you may be eligible to receive."
                + "<br><br>Alternatively, if you have already been receiving benefit payments from us, we may need this information to ensure you have received the correct benefit payments for the prior financial year/s."
                + "<br><br>For privacy protection, please make sure your Tax File Number (TFN) is removed or blacked out. "
        case "GreenID - IV Consent Form":
            return "Please provide the completed Identification Verification Consent Form and an uncertified copy of evidence of your full name and date of birth (e.g. Birth Certificate, Driver’s Licence, Passport). Green ID is an online identity verification program which confirms your identity details against Government held records. It is a secure and convenient way to check your identification details without requiring a certified copy.";
        default:
            return "Please upload documents to progress this requirement. If you have already actioned this requirement, we will be in touch over the coming days once our review is completed."
    }
};

const getRequirementExplanationWithoutUpload = (requirement: string | undefined) => {
    switch (requirement) {
        case "EFT Details":
            return "Please complete this form with your bank account details so we can deposit any benefit payments that you’re eligible to receive.";
        case "Payment details of client/beneficiary":
            return "Please complete this form with your bank account details so we can deposit any benefit payments that you’re eligible to receive.";
        case "Tax File Number Declaration":
            return "Please complete and sign the Tax File Number (TFN) declaration. This will provide us with your TFN details so we can withhold the appropriate PAYG tax if any benefits are payable. Please indicate whether you are claiming the tax-free threshold so we can accurately calculate any tax payable to the Australian Tax Office.";
        case "Progress call appointment":
            return "Your Claims Consultant will call you for an update on your ongoing treatment and recovery. This will allow us to confirm your entitlement to continue receiving benefits.";
        default:
            return "Please upload documents to progress this requirement. If you have already actioned this requirement, we will be in touch over the coming days once our review is completed."
    }
};

const RequirementUploadDocument = ({ requirement, onDisableCollapse }: RequirementUploadDocumentProps) => {
    const { global: { sizeBaseUnit, fontSizeBodyLarge } } = getGelTokens()
    const requirementExplanation = getRequirementExplanation(requirement.friendlyText?.trim())

    return (<div data-testid="requirement-upload-document" data-upload-root="true">
        <GelRowLayout gutter="xsmall">
            <GelParagraph style={{
                fontWeight: '400px', fontSize: fontSizeBodyLarge,
                paddingTop: sizeBaseUnit * 4, paddingBottom: sizeBaseUnit * 2
            }}>
                <div dangerouslySetInnerHTML={{ __html: requirementExplanation }} />
            </GelParagraph>
        </GelRowLayout>
        <Uploader
            requirement={requirement}
            setAllowCollapse={onDisableCollapse}
        />
    </div>)
}
